import { Editor } from "@tiptap/core";
import { NodeSelection, type Transaction } from "@tiptap/pm/state";
import { isInTable } from "@tiptap/pm/tables";
import type { JSX } from "preact";
import { useEffect, useId, useRef, useState } from "preact/hooks";
import { ACCEPTED_MIME, type Doc } from "../../../shared/types.ts";
import { addTableRow, noteExtensions, tableDoc, WikiLinks } from "./detail/extensions.ts";
import { IconBold, IconChain, IconHeading, IconItalic, IconMarker, IconQuote, IconTask } from "./detail/icons.tsx";
import { noteByTitle, titledNotes } from "./detail/links.ts";
import { linkHref } from "./detail/rules.ts";
import { isTypingTarget } from "./lib.ts";
import { showCard, store, toast } from "./state.ts";

type Props = {
  doc: Doc | null;
  label: string;
  placeholder: string;
  id?: string;
  /** Chamado ao sair do editor (e ao desmontar) se o texto mudou. */
  onSave?: (doc: Doc, text: string) => void;
  /** Ctrl/Cmd+Enter. Sem onSubmit, Ctrl/Cmd+Enter só salva. */
  onSubmit?: (doc: Doc, text: string, editor: Editor) => void;
  /** Esc tira o foco (nota rápida). No detalhe, Esc fica para fechar o diálogo. */
  blurOnEsc?: boolean;
  onReady?: (editor: Editor) => void;
  onEmpty?: (empty: boolean) => void;
  /** [[ abre a lista de notas com título; clique em [[Título]] abre a nota. `cardId` = o próprio card (fica fora). */
  links?: boolean;
  cardId?: string;
  /** "/" abre os comandos (título, tarefas, imagem, tabela...); colar ou soltar imagem entra na nota. */
  slash?: boolean;
  /** O diálogo que abre com este editor foca o texto (modo escrita), não o primeiro botão. */
  autofocus?: boolean;
};

type Item = { key: string; label: string; hint?: string; run: (ed: Editor) => void };
type Menu = { kind: "wiki" | "slash"; from: number; to: number; items: Item[]; index: number; style: string };
/**
 * Barra de formatação: posição (fixa na tela), o que está ligado na seleção e o endereço do link sob ela. No toque fica
 * presa embaixo, logo acima do teclado ("docked"): em cima da seleção ficam o menu do sistema e as alças de seleção.
 */
type Bar = { style: string; pos: "up" | "below" | "docked"; active: Record<string, boolean>; href: string };

const IMAGE_MIME = ACCEPTED_MIME.filter((m) => m.startsWith("image/")) as string[];

/**
 * Parte visível da tela, em coordenadas da janela de layout (as de getBoundingClientRect e de position: fixed). O teclado
 * na tela não encolhe innerHeight no iOS nem no Chrome do Android: só a visualViewport sabe onde ele começa.
 */
const visibleTop = () => window.visualViewport?.offsetTop ?? 0;
export const visibleBottom = () => {
  const vv = window.visualViewport;
  return vv ? vv.offsetTop + vv.height : innerHeight;
};
/** Lista presa a um campo: embaixo dele; sem espaço até o teclado, em cima. A altura cabe no espaço que sobrou. */
export function popPlace(at: { top: number; bottom: number }, want = 300): string {
  const below = visibleBottom() - at.bottom - 12;
  const above = at.top - visibleTop() - 12;
  const up = below < want && above > below;
  const max = Math.round(Math.max(96, Math.min(want, up ? above : below)));
  return up ? `top:${Math.round(at.top - 6)}px;translate:0 -100%;max-height:${max}px` : `top:${Math.round(at.bottom + 6)}px;max-height:${max}px`;
}
/** Barra de formatação presa acima do teclado: 44 + 2 × 4 de altura, mais a folga até a seleção. */
const DOCKED = 52 + 8;
/** Rola a folha (detalhe, modo escrita) até `bottom` ficar `room` px acima do fim da parte visível. */
function lift(el: Element, bottom: number, room: number) {
  const over = bottom + room - visibleBottom();
  if (over > 0) el.closest(".detail-box, .rd-writer")?.scrollBy(0, over);
}
/**
 * Teclado abrindo sobre o campo em edição (detalhe, modo escrita): a folha já ganhou embaixo o espaço que o teclado
 * cobre (--kb, medido em state.ts, que ouve antes deste) e rola até o fim da seleção aparecer, acima da barra se ela
 * estiver presa ali.
 */
window.visualViewport?.addEventListener("resize", () => {
  const el = document.activeElement as HTMLElement | null;
  if (!el?.closest("dialog[open]") || !isTypingTarget(el) || el.closest(".fmt-bar")) return; // a barra (link) segue o teclado sozinha
  const sel = getSelection();
  const caret = el.isContentEditable && sel?.rangeCount ? [...sel.getRangeAt(0).getClientRects()].pop() : undefined;
  lift(el, (caret ?? el.getBoundingClientRect()).bottom, document.querySelector(".fmt-bar.is-docked") ? DOCKED : 12);
});
// Não importa MOD do Grid.tsx: o Grid importa este arquivo (ciclo) e a lista abaixo é montada no carregamento.
const MAC = /Mac|iPhone|iPad/.test(navigator.userAgent);

/** Imagens subindo, por editor: o editor mostra "Enviando imagem…" e a nota rápida espera antes de guardar. */
const sending = new WeakMap<Editor, { jobs: Set<Promise<void>>; files: number; show: (n: number) => void }>();
/** Espera as imagens coladas, soltas ou escolhidas neste editor entrarem na nota (ou falharem, com aviso). */
export async function imagesSent(ed: Editor) {
  for (let jobs; (jobs = sending.get(ed)?.jobs)?.size; ) await Promise.all(jobs);
}

/**
 * Sobe as imagens (blobs da própria conta) e põe na nota em `pos` (padrão: o cursor), todas juntas e na ordem. A
 * posição acompanha o que mudou enquanto subiam (mapping de cada transação; -1: o texto digitado depois fica depois).
 * Falha no envio: o store avisa; imagem que não coube mais na nota (editor fechado): aviso aqui, nunca some calada.
 */
function insertImages(ed: Editor, files: File[], pos = ed.state.selection.from) {
  const images = files.filter((x) => IMAGE_MIME.includes(x.type));
  const track = sending.get(ed);
  if (!images.length || !track) return;
  let at = pos;
  const follow = ({ transaction }: { transaction: Transaction }) => void (at = transaction.mapping.map(at, -1));
  ed.on("transaction", follow);
  const job: Promise<void> = Promise.allSettled(images.map((f) => store.uploadBlob(f))).then((done) => {
    ed.off("transaction", follow);
    const nodes = done.flatMap((r, i) => (r.status === "fulfilled" ? [{ type: "image", attrs: { src: `/media/${r.value}`, alt: images[i].name.replace(/\.[^.]+$/, "") } }] : []));
    const unseen = done.some((r) => r.status === "rejected" && (r.reason as Error)?.cause !== "notified");
    let placed = !nodes.length;
    try {
      placed ||= !ed.isDestroyed && ed.commands.insertContentAt(Math.min(at, ed.state.doc.content.size), nodes);
    } catch {} // posição que não aceita a imagem: avisa abaixo (e a nota rápida não fica esperando para sempre)
    if (unseen || !placed) toast({ key: "image-lost", text: "A imagem não entrou na nota. Cole de novo." });
    track.jobs.delete(job);
    track.show((track.files -= images.length));
  });
  track.jobs.add(job);
  track.show((track.files += images.length));
}

function pickImages(ed: Editor) {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = IMAGE_MIME.join(",");
  input.multiple = true;
  input.onchange = () => insertImages(ed, [...(input.files ?? [])]);
  input.click();
}

const SLASH: (Item & { keys: string; when?: (ed: Editor) => boolean })[] = [
  { key: "titulo", label: "Título", hint: "Texto grande de seção", keys: "titulo heading cabecalho", run: (ed) => ed.chain().focus().toggleHeading({ level: 2 }).run() },
  { key: "tarefas", label: "Lista de tarefas", hint: "Itens para marcar como feitos", keys: "tarefas checklist todo afazeres", run: (ed) => ed.chain().focus().toggleTaskList().run() },
  { key: "lista", label: "Lista", hint: "Itens com marcador", keys: "lista marcadores bullet", run: (ed) => ed.chain().focus().toggleBulletList().run() },
  { key: "citacao", label: "Citação", hint: "Trecho recuado", keys: "citacao aspas quote", run: (ed) => ed.chain().focus().toggleBlockquote().run() },
  { key: "marca", label: "Marca-texto", hint: "Também com ==assim==", keys: "marca texto destaque highlight", run: (ed) => ed.chain().focus().toggleMark("highlight").run() },
  { key: "codigo", label: "Bloco de código", keys: "codigo code", run: (ed) => ed.chain().focus().toggleCodeBlock().run() },
  { key: "divisoria", label: "Divisória", keys: "divisoria linha separador", run: (ed) => ed.chain().focus().setHorizontalRule().run() },
  { key: "imagem", label: "Imagem", hint: "Do computador ou do celular", keys: "imagem foto figura", run: pickImages },
  { key: "tabela", label: "Tabela", hint: "3 × 3, com cabeçalho", keys: "tabela grade", run: (ed) => ed.chain().focus().insertContent(tableDoc()).run() },
  { key: "linha", label: "Nova linha na tabela", keys: "linha tabela", when: (ed) => isInTable(ed.state), run: (ed) => void addTableRow(ed.state, ed.view.dispatch) },
];
const fold = (s: string) => s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();

/** Barra ao selecionar (§4.1): os botões rodam o comando sobre a seleção; o link abre um campo na própria barra. */
type Tool = { key: string; mark: string; label: string; keys?: string; icon: () => JSX.Element; run?: (ed: Editor) => boolean };
const TOOLS: Tool[] = [
  { key: "bold", mark: "bold", label: "Negrito", keys: "Mod+B", icon: IconBold, run: (ed) => ed.chain().toggleBold().run() },
  { key: "italic", mark: "italic", label: "Itálico", keys: "Mod+I", icon: IconItalic, run: (ed) => ed.chain().toggleItalic().run() },
  { key: "highlight", mark: "highlight", label: "Marca-texto", keys: "Mod+Shift+H", icon: IconMarker, run: (ed) => ed.chain().toggleMark("highlight").run() },
  { key: "heading", mark: "heading", label: "Título", icon: IconHeading, run: (ed) => ed.chain().toggleHeading({ level: 2 }).run() },
  { key: "task", mark: "taskList", label: "Tarefa", icon: IconTask, run: (ed) => ed.chain().toggleTaskList().run() },
  { key: "quote", mark: "blockquote", label: "Citação", keys: "Mod+Shift+B", icon: IconQuote, run: (ed) => ed.chain().toggleBlockquote().run() },
  { key: "link", mark: "link", label: "Link", keys: "Mod+K", icon: IconChain },
];
const tipOf = (t: Tool) => (t.keys ? `${t.label} (${t.keys.replace("Mod", MAC ? "⌘" : "Ctrl")})` : t.label);
const ariaKeys = (t: Tool) => t.keys?.replace("Mod", MAC ? "Meta" : "Control");

// TipTap (StarterKit + tarefas, marca-texto, imagem e tabela). Conteúdo lido só na montagem:
// ponytail: edição remota com o editor aberto não reaparece; quem usa dá key={card.id} para remontar.
export function RichText(p: Props) {
  const host = useRef<HTMLDivElement>(null);
  const props = useRef(p);
  props.current = p;
  const [empty, setEmpty] = useState(!p.doc?.content?.length);
  const [menu, setMenu] = useState<Menu | null>(null);
  const menuRef = useRef<Menu | null>(null);
  menuRef.current = menu;
  const [bar, setBar] = useState<Bar | null>(null);
  const barNow = useRef<Bar | null>(null);
  barNow.current = bar;
  const [linking, setLinking] = useState(false);
  const linkingNow = useRef(false);
  const [linkError, setLinkError] = useState(false);
  const [uploads, setUploads] = useState(0);
  const barEl = useRef<HTMLDivElement>(null);
  const linkInput = useRef<HTMLInputElement>(null);
  const barDismissed = useRef<string | null>(null); // Esc esconde a barra até a seleção mudar (from:to)
  const lifted = useRef<string | null>(null); // seleção (from:to) que a folha já subiu para mostrar acima da barra
  const ed = useRef<Editor | null>(null);
  const dismissed = useRef<string | null>(null); // Esc fecha a lista até o gatilho mudar (tipo:posição)
  const listId = useId();
  const errId = useId();

  const choose = (i: number) => {
    const m = menuRef.current;
    const editor = ed.current;
    const it = m?.items[i];
    if (!m || !editor || !it) return;
    setMenu(null);
    editor.chain().focus().deleteRange({ from: m.from, to: m.to }).run();
    it.run(editor);
  };

  // Gatilhos antes do cursor: "[[texto" (ligação) e "/comando" no começo ou depois de espaço.
  const check = (editor: Editor) => {
    const { selection } = editor.state;
    const $f = selection.$from;
    const off = () => void (menuRef.current && setMenu(null));
    if (!selection.empty || !$f.parent.isTextblock || $f.parent.type.spec.code) return off();
    const before = $f.parent.textBetween(Math.max(0, $f.parentOffset - 80), $f.parentOffset, undefined, "￼");
    let kind: Menu["kind"] | null = null;
    let from = 0;
    let items: Item[] = [];
    let m: RegExpExecArray | null;
    if (props.current.links && (m = /\[\[([^[\]\n]{0,60})$/.exec(before))) {
      kind = "wiki";
      from = $f.pos - m[0].length;
      items = titledNotes(m[1], props.current.cardId).map((c) => ({ key: c.id, label: c.title!, run: (e: Editor) => void e.chain().focus().insertContent(`[[${c.title}]] `).run() }));
    } else if (props.current.slash && (m = /(?:^|\s)\/([\p{L}\d]{0,20})$/u.exec(before))) {
      kind = "slash";
      from = $f.pos - m[1].length - 1;
      const q = fold(m[1]);
      items = SLASH.filter((s) => (!s.when || s.when(editor)) && (fold(s.label).includes(q) || s.keys.includes(q)));
    }
    if (dismissed.current !== `${kind}:${from}`) dismissed.current = null; // gatilho novo (ou nenhum): vale de novo
    if (!kind || dismissed.current) return off();
    const at = editor.view.coordsAtPos(from);
    const prev = menuRef.current;
    const index = prev && prev.kind === kind && prev.from === from ? Math.min(prev.index, Math.max(0, items.length - 1)) : 0;
    setMenu({ kind, from, to: $f.pos, items, index, style: `left:${Math.round(Math.max(8, Math.min(at.left, innerWidth - 288)))}px;${popPlace(at)}` });
  };

  // Barra sobre a seleção não vazia (ou no cursor, com o campo de link aberto). Some fora do editor e da própria barra.
  const place = (editor: Editor) => {
    const { selection } = editor.state;
    const { from, to, empty, $from } = selection;
    const key = `${from}:${to}`;
    if (barDismissed.current !== key) barDismissed.current = null;
    const focused = editor.view.hasFocus() || !!barEl.current?.contains(document.activeElement);
    const want =
      editor.isEditable &&
      !$from.parent.type.spec.code &&
      (linkingNow.current || (!empty && !(selection instanceof NodeSelection) && !barDismissed.current && focused));
    if (!want) {
      lifted.current = null;
      return void (barNow.current && setBar(null));
    }
    const active = Object.fromEntries(TOOLS.map((t) => [t.key, editor.isActive(t.mark)]));
    const href = String(editor.getAttributes("link").href ?? "");
    // Toque: presa acima do teclado; o menu do sistema e as alças ficam com a seleção. Seleção nova embaixo da barra: a
    // folha sobe até ela aparecer (só quando a seleção muda; rolar a folha à mão não é desfeito).
    if (matchMedia("(pointer: coarse)").matches) {
      if (lifted.current !== key) lift(editor.view.dom, editor.view.coordsAtPos(to, -1).bottom, DOCKED);
      lifted.current = key;
      return setBar({ style: `top:${Math.round(visibleBottom())}px`, pos: "docked", active, href });
    }
    const a = editor.view.coordsAtPos(from);
    const b = empty ? a : editor.view.coordsAtPos(to);
    const half = (linkingNow.current ? 340 : 270) / 2;
    const mid = (Math.min(a.left, b.left) + Math.max(a.right, b.right)) / 2;
    const x = Math.round(Math.max(half + 8, Math.min(mid, innerWidth - half - 8)));
    const up = a.top > 64; // sem espaço em cima (perto do topo): embaixo da seleção
    setBar({ style: up ? `left:${x}px;top:${Math.round(a.top - 8)}px;translate:-50% -100%` : `left:${x}px;top:${Math.round(b.bottom + 8)}px`, pos: up ? "up" : "below", active, href });
  };
  const hideBar = () => {
    linkingNow.current = false;
    setLinking(false);
    setLinkError(false);
    setBar(null);
  };
  /** Esc: a barra some até a seleção mudar e o foco volta ao texto (síncrono: o campo do link some logo depois). */
  const dismissBar = () => {
    const editor = ed.current;
    if (!editor) return;
    const { from, to } = editor.state.selection;
    barDismissed.current = `${from}:${to}`;
    hideBar();
    editor.view.focus();
  };
  const openLink = (editor: Editor) => {
    if (editor.isActive("link")) editor.commands.extendMarkRange("link"); // editar ou tirar o link inteiro
    linkingNow.current = true;
    setLinking(true);
    setLinkError(false);
    // Campo já montado (escondido) na barra: o foco sai dentro do toque, e o iPhone abre o teclado nele.
    const field = linkInput.current;
    if (field) {
      field.value = String(editor.getAttributes("link").href ?? "");
      field.focus();
      field.select();
    }
    place(editor);
  };
  const closeLink = (editor: Editor) => {
    linkingNow.current = false;
    setLinking(false);
    setLinkError(false);
    editor.view.focus();
    place(editor);
  };
  const applyLink = (e: Event) => {
    e.preventDefault();
    const editor = ed.current;
    if (!editor) return;
    const raw = linkInput.current?.value ?? "";
    if (!raw.trim()) return removeLink();
    const href = linkHref(raw);
    if (!href) return setLinkError(true);
    const bare = editor.state.selection.empty && !editor.isActive("link");
    closeLink(editor);
    if (bare) editor.chain().insertContent({ type: "text", text: href, marks: [{ type: "link", attrs: { href } }] }).run();
    else editor.chain().extendMarkRange("link").setLink({ href }).run();
  };
  const removeLink = () => {
    const editor = ed.current;
    if (!editor) return;
    closeLink(editor);
    editor.chain().extendMarkRange("link").unsetLink().run();
  };
  const runTool = (t: Tool) => {
    const editor = ed.current;
    if (!editor) return;
    if (!t.run) return openLink(editor);
    editor.view.focus(); // síncrono: o botão focado pelo teclado devolve o foco ao texto antes do comando
    t.run(editor);
  };
  // Teclado na barra (toolbar ARIA): ←/→, Home/End andam entre os botões; Esc volta ao texto.
  const onBarKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault(); // no detalhe e no modo escrita, Esc fecharia o diálogo
      return dismissBar();
    }
    if (linkingNow.current) return;
    const btns = [...(barEl.current?.querySelectorAll<HTMLButtonElement>(".fmt-btn") ?? [])];
    const i = btns.indexOf(document.activeElement as HTMLButtonElement);
    const n = btns.length;
    const j = { ArrowRight: (i + 1) % n, ArrowLeft: (i + n - 1) % n, Home: 0, End: n - 1 }[e.key];
    if (j === undefined || i < 0) return;
    e.preventDefault(); // as setas também trocariam de card no detalhe
    btns.forEach((b, k) => (b.tabIndex = k === j ? 0 : -1));
    btns[j].focus();
  };

  useEffect(() => {
    let dirty = false;
    let broken = false;
    const doc = () => editor.getJSON() as Doc;
    const flush = () => {
      if (!dirty || broken) return;
      dirty = false;
      props.current.onSave?.(doc(), editor.getText());
    };
    const openWiki = (title: string) => {
      const c = noteByTitle(title, props.current.cardId);
      if (!c) toast({ key: "wiki-miss", text: `Nenhuma nota com o título “${title}”.` });
      else showCard(c.id);
      return !!c;
    };
    const editor: Editor = new Editor({
      element: host.current!,
      extensions: [...noteExtensions, ...(p.links ? [WikiLinks.configure({ open: openWiki })] : [])],
      content: (p.doc ?? "") as never,
      editorProps: {
        attributes: { role: "textbox", "aria-multiline": "true", "aria-label": p.label, ...(p.id ? { id: p.id } : {}), ...(p.autofocus ? { autofocus: "" } : {}) },
        handleDOMEvents: {
          // Sem blurOnEsc o Esc não é do editor: true aqui pula o keymap do ProseMirror (Escape = selectParentNode, que
          // dava preventDefault) sem cancelar o evento, e ele chega ao <dialog> do detalhe, que fecha.
          // Com a lista de [[ ou "/" aberta (ou a barra de formatação), o Esc só fecha a lista (o preventDefault segura o diálogo).
          keydown: (_view, e) => {
            if (e.key !== "Escape") return false;
            if (menuRef.current) {
              e.preventDefault();
              dismissed.current = `${menuRef.current.kind}:${menuRef.current.from}`;
              setMenu(null);
              return true;
            }
            if (barNow.current) {
              e.preventDefault();
              dismissBar();
              return true;
            }
            return !props.current.blurOnEsc;
          },
        },
        handleKeyDown: (_view, e) => {
          const m = menuRef.current;
          if (m && !e.ctrlKey && !e.metaKey && !e.altKey) {
            const n = m.items.length;
            if ((e.key === "ArrowDown" || e.key === "ArrowUp") && n) {
              setMenu({ ...m, index: (m.index + (e.key === "ArrowDown" ? 1 : n - 1)) % n });
              return true;
            }
            if ((e.key === "Enter" || e.key === "Tab") && n && !e.shiftKey) {
              choose(m.index);
              return true;
            }
          }
          if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            const sub = props.current.onSubmit;
            if (sub) sub(doc(), editor.getText(), editor);
            else flush();
            return true;
          }
          // Ctrl/Cmd+K: campo de link (criar, editar, tirar). true = preventDefault (o Chrome focaria a busca).
          if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "k") {
            openLink(editor);
            return true;
          }
          // Alt+F10 leva o foco à barra (padrão ARIA de barra de ferramentas); Tab também chega nela.
          if (e.altKey && e.key === "F10" && barNow.current) {
            barEl.current?.querySelector<HTMLElement>("[tabindex='0']")?.focus();
            return true;
          }
          if (e.key === "Escape" && props.current.blurOnEsc) {
            // Síncrono: commands.blur() espera um frame e derrubaria um foco dado logo depois (Esc, N).
            (editor.view.dom as HTMLElement).blur();
            return true;
          }
          return false;
        },
        handlePaste: (view, e) => {
          const files = [...(e.clipboardData?.files ?? [])];
          if (!props.current.slash || !files.some((f) => IMAGE_MIME.includes(f.type))) return false;
          insertImages(editor, files, view.state.selection.from);
          return true;
        },
        handleDrop: (view, e, _slice, moved) => {
          const files = [...(e.dataTransfer?.files ?? [])];
          if (moved || !props.current.slash || !files.some((f) => IMAGE_MIME.includes(f.type))) return false;
          insertImages(editor, files, view.posAtCoords({ left: e.clientX, top: e.clientY })?.pos);
          return true;
        },
      },
      onUpdate: () => {
        dirty = true;
        setEmpty(editor.isEmpty);
        props.current.onEmpty?.(editor.isEmpty);
        check(editor);
        place(editor);
      },
      onSelectionUpdate: () => {
        check(editor);
        place(editor);
      },
      onFocus: () => place(editor),
      onBlur: ({ event }) => {
        flush();
        setMenu(null);
        // Foco indo para a barra (Tab, Alt+F10, campo do link): ela fica.
        if (!barEl.current?.contains(event.relatedTarget as Node | null)) hideBar();
      },
    });
    ed.current = editor;
    sending.set(editor, { jobs: new Set(), files: 0, show: setUploads });
    // Doc fora do schema abre vazio (o TipTap descarta) e o primeiro toque gravaria por cima: fica só leitura e nunca salva.
    try {
      if (p.doc) editor.schema.nodeFromJSON(p.doc).check();
    } catch {
      broken = true;
      editor.setEditable(false, false);
    }
    props.current.onReady?.(editor);
    return () => {
      flush();
      editor.destroy();
      ed.current = null;
    };
  }, []);

  // Lista ligada ao campo: aria-activedescendant aponta a opção marcada (o foco fica no texto).
  useEffect(() => {
    const dom = ed.current?.view.dom;
    if (!dom) return;
    const set = (k: string, v: string | null) => (v === null ? dom.removeAttribute(k) : dom.setAttribute(k, v));
    set("aria-autocomplete", menu ? "list" : null);
    set("aria-controls", menu ? listId : null);
    set("aria-expanded", menu ? "true" : null);
    set("aria-activedescendant", menu?.items.length ? `${listId}-${menu.index}` : null);
  }, [menu]);

  // Campo do link aberto por Ctrl/Cmd+K sem barra na tela (o campo nasceu agora): foco nele, endereço selecionado.
  useEffect(() => {
    if (!linking || document.activeElement === linkInput.current) return;
    linkInput.current?.focus();
    linkInput.current?.select();
  }, [linking]);
  // A barra e a lista acompanham a seleção quando a folha rola, a janela muda ou o teclado na tela abre e fecha.
  useEffect(() => {
    if (!bar && !menu) return;
    const again = () => {
      const editor = ed.current;
      if (!editor) return;
      if (menuRef.current) check(editor);
      if (barNow.current) place(editor);
    };
    const vv = window.visualViewport;
    addEventListener("scroll", again, true);
    addEventListener("resize", again);
    vv?.addEventListener("resize", again);
    vv?.addEventListener("scroll", again);
    return () => {
      removeEventListener("scroll", again, true);
      removeEventListener("resize", again);
      vv?.removeEventListener("resize", again);
      vv?.removeEventListener("scroll", again);
    };
  }, [!!bar, !!menu]);

  return (
    <>
      <div class="rich" ref={host} data-empty={String(empty)} data-placeholder={p.placeholder} />
      <p class="rich-sending" role="status">
        {uploads ? (uploads > 1 ? `Enviando ${uploads} imagens…` : "Enviando imagem…") : ""}
      </p>
      {bar && (
        <div
          ref={barEl}
          class={`fmt-bar${bar.pos === "up" ? "" : ` is-${bar.pos}`}`}
          role="toolbar"
          aria-label="Formatar o texto selecionado"
          style={bar.style}
          onKeyDown={onBarKey}
          onMouseDown={(e) => e.target === e.currentTarget && e.preventDefault()}
          onFocusOut={(e) => {
            const to = e.relatedTarget as Node | null;
            if (!e.currentTarget.contains(to) && to !== ed.current?.view.dom) hideBar();
          }}
        >
          {/* O campo do link fica montado (escondido) com a barra: o botão Link o foca dentro do toque. */}
          <form class={`fmt-link${linking ? "" : " is-idle"}`} noValidate onSubmit={applyLink}>
            <input
              ref={linkInput}
              class="fmt-link-input"
              tabIndex={linking ? 0 : -1}
              aria-hidden={linking ? undefined : "true"}
              type="text"
              inputMode="url"
              aria-label="Endereço do link"
              aria-invalid={linkError || undefined}
              aria-describedby={linkError ? errId : undefined}
              placeholder="Cole ou digite o endereço"
              defaultValue={bar.href}
              autoComplete="off"
              spellcheck={false}
              enterKeyHint="done"
              onInput={() => linkError && setLinkError(false)}
            />
            {linking && (
              <>
                <button type="submit" class="fmt-text">
                  Aplicar
                </button>
                {bar.active.link && (
                  <button type="button" class="fmt-text" onClick={removeLink}>
                    Tirar link
                  </button>
                )}
                {linkError && (
                  <p id={errId} class="fmt-err" role="alert">
                    Endereço inválido. Use um site (exemplo.com) ou um link http.
                  </p>
                )}
              </>
            )}
          </form>
          {!linking &&
            TOOLS.map((t, i) => (
              <button
                key={t.key}
                type="button"
                class="fmt-btn"
                tabIndex={i ? -1 : 0}
                aria-label={t.label}
                aria-pressed={t.run ? bar.active[t.key] : undefined}
                aria-keyshortcuts={ariaKeys(t)}
                data-tip={tipOf(t)}
                onMouseDown={(e) => e.preventDefault()} // a seleção e o foco ficam no texto
                onClick={() => runTool(t)}
              >
                <t.icon />
              </button>
            ))}
        </div>
      )}
      {menu && (
        <ul
          class="suggest"
          id={listId}
          role="listbox"
          aria-label={menu.kind === "wiki" ? "Notas para ligar" : "Comandos"}
          style={menu.style}
        >
          {menu.kind === "wiki" && (
            <li class="suggest-head" role="presentation">
              Ligar a uma nota
            </li>
          )}
          {menu.items.map((it, i) => (
            <li
              key={it.key}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === menu.index}
              class="suggest-item"
              onMouseDown={(e) => {
                e.preventDefault(); // o foco fica no texto
                choose(i);
              }}
              onMouseMove={() => i !== menu.index && setMenu({ ...menu, index: i })}
            >
              <span class="suggest-label">{it.label}</span>
              {it.hint && <span class="suggest-hint">{it.hint}</span>}
            </li>
          ))}
          {!menu.items.length && (
            <li class="suggest-empty" role="option" aria-disabled="true" aria-selected="false">
              {menu.kind === "wiki" ? "Nenhuma nota com título parecido." : "Nenhum comando com esse nome."}
            </li>
          )}
        </ul>
      )}
    </>
  );
}
